import * as React from "react";

import { AsyncComponent3 } from "@impero-com/common/components/asyncComponent";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import {
    withIdParams,
    withRouter,
} from "@impero-com/common/components/routerUtils";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { logAndThrow } from "@impero-com/common/utils/error";
import { formatUserName } from "@impero-com/common/utils/formatting";
import withI18n, { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Button,
    emitNotification,
    FormFrame,
    PageSpinner,
} from "@impero-com/ui";
import { Table, Spin } from "antd";
import type { ColumnType } from "antd/es/table/interface";
import Transfer, { TransferDirection, TransferItem } from "antd/es/transfer";
import { remove, difference } from "lodash-es";
import memoizeOne from "memoize-one";
import { NavigateFunction } from "react-router-dom";

import {
    UserGroupViewDto as UserGroupWithDetails,
    UserGroupsUserView,
    UserOverview,
    UpdateUserGroupBody,
    UserTagOverviewListDto as Tags,
} from "api/generated/model";
import { getTags } from "api/generated/tags/tags";
import {
    getUserGroupsGroupId,
    patchUserGroupsGroupId,
} from "api/generated/user-groups/user-groups";
import { getUsers } from "api/generated/users/users";
import { EditableBaseTagListPlus } from "components/tag/editableBaseTagListPlus/editableTagListPlus";
import { UserTag } from "components/tag/editableBaseTagListPlus/types";
import {
    getUserTagCategoryId,
    isUserTagActive,
} from "components/tag/editableBaseTagListPlus/utils";
import { ActivableTag, TagCategory } from "components/tag/types";
import type { UserListResponseOverview } from "org/user/list/models";
import {
    casterWithIdWrapper,
    castValue,
    FormComponentProps,
    FormCreate,
    FormRootComponentProps,
} from "utils/form";
import { Guid, generate } from "utils/guid";

import { WebRoute } from "../../../routes";

import {
    USER_GROUP_DETAILS_TAB,
    USER_GROUP_MEMBERS_TAB,
    UserGroupsApplicationBar,
} from "./manager";
import * as theme from "./theme.css";

interface UserGroupCreateMembersValues {
    members: UserGroupsUserView[];
}

interface UserGroupDetailsProps {
    ajaxManager: AjaxManager;
    navigate: NavigateFunction;
    id: string;
}

type UserGroupEditorStatus = { type: "DEFAULT" } | { type: "SUBMITTING" };

export interface UserViewForTransferComp {
    key: string;
    userOverview: UserOverview;
}

export interface UserGroupsUserViewForTransferComp {
    key: string;
    userOverview: UserOverview;
    userGroupsUserView: UserGroupsUserView;
}

interface UserForTransferComp {
    type: "User";
    value: UserViewForTransferComp;
}
interface UserGroupUserForTransferComp {
    type: "UserGroupUser";
    value: UserGroupsUserViewForTransferComp;
}

type UserOrUserGroupUser = UserForTransferComp | UserGroupUserForTransferComp;

interface UserGroupDetailsState {
    status: UserGroupEditorStatus;
}

interface UserGroupTransferValues {
    userGroupUsers: UserGroupsUserView[];
}

interface UserTableTransferPropsBase<
    ID extends string,
    R,
> extends FormComponentProps<ID, UserGroupsUserView[], R> {
    id: ID;
    ajaxManager: AjaxManager;
    allTags: Tags;
    allUsers: UserOverview[];
}

type UserTableTransferPropsMode =
    | {
          type: "EDIT";
          defaultMembers: UserGroupsUserView[];
      }
    | {
          type: "CREATE";
      };

type UserTableTransferProps<ID extends string, R> = UserTableTransferPropsBase<
    ID,
    R
> &
    UserTableTransferPropsMode;

interface UserTableTransferState {
    existingUserGroupUserIds: string[];
}

class UserTableTransfer<ID extends string, R> extends React.PureComponent<
    UserTableTransferProps<ID, R>,
    UserTableTransferState
> {
    constructor(props: UserTableTransferProps<ID, R>) {
        super(props);
        let existingUserGroupUserIds: string[] = [];
        switch (props.type) {
            case "EDIT":
                existingUserGroupUserIds = props.defaultMembers.map(
                    (ugu) => ugu.id
                );
                break;
            case "CREATE":
                const userGroupUsers = props.form.getFieldValue(props.id) || [];
                existingUserGroupUserIds = userGroupUsers.map((ugu) => ugu.id);
                break;
            default:
                assertNever(props);
        }
        this.state = {
            existingUserGroupUserIds,
        };
    }

    handleChange = (
        newExistingUserGroupUserIds: string[],
        direction: TransferDirection,
        moveKeys: string[]
    ) => {
        const { allUsers, form, id } = this.props;
        const userGroupUsers = form.getFieldValue(id) || [];

        switch (direction) {
            case "right": {
                // we find the users in order to create the user group users
                moveKeys.forEach((changedKey) => {
                    const user = allUsers.find(
                        (u) => u.id.toString() === changedKey
                    );
                    if (user !== undefined) {
                        const newUgu = {
                            id: generate(),
                            userId: user.id,
                            userTagIds: [],
                        };
                        userGroupUsers.push(newUgu);

                        // Replace user for newly created user group user in order to make it appear on the right side of transfer
                        const userToExchangeForAnUserGroupUserIndex =
                            newExistingUserGroupUserIds.indexOf(
                                user.id.toString()
                            );
                        if (userToExchangeForAnUserGroupUserIndex !== -1) {
                            newExistingUserGroupUserIds[
                                userToExchangeForAnUserGroupUserIndex
                            ] = newUgu.id;
                        }
                    }
                });
                break;
            }
            case "left": {
                // we find the user group users to remove
                moveKeys.forEach((changedKey) => {
                    remove(userGroupUsers, (ugu) => ugu.id === changedKey);
                });
                break;
            }
            default:
                assertNever(direction);
        }

        form.setFieldsValue(
            casterWithIdWrapper<UserGroupsUserView[]>()(
                id,
                form,
                userGroupUsers
            )
        );
        this.setState({
            existingUserGroupUserIds: newExistingUserGroupUserIds,
        });
    };

    private renderUserInfo() {
        return (_value: any, record: TransferItem) => {
            const userOverview: UserOverview = record.value.userOverview;
            return (
                <>
                    <div className={theme.user}>
                        {formatUserName(userOverview)}
                    </div>
                    <div className={theme.email}>{userOverview.email}</div>
                </>
            );
        };
    }

    private renderUserTags(
        allTags: Array<ActivableTag>,
        allTagCategories: Array<TagCategory>
    ) {
        const allUserTags: UserTag[] = allTags.map((tag) => ({
            ...tag,
            type: "user",
        }));
        return (record: TransferItem) => {
            const userGroupsUserView: UserGroupsUserView =
                record.value.userGroupsUserView;
            return (
                <EditableBaseTagListPlus<UserTag>
                    selectedTagIds={userGroupsUserView.userTagIds}
                    setSelectedTagIds={this.updateTagsInUserGroupUser(
                        record.key
                    )}
                    allTags={allUserTags}
                    allTagCategories={allTagCategories}
                    isTagActive={isUserTagActive}
                    getTagCategoryId={getUserTagCategoryId}
                    disabled={false}
                    canCreateTag={false}
                    onCreateTag={() => {}}
                    getParentContainer={() => document.body}
                />
            );
        };
    }

    private updateTagsInUserGroupUser(userGroupUserId: Guid | undefined) {
        const { id, form } = this.props;
        const value: UserGroupsUserView[] = form.getFieldValue(id) ?? [];

        return (tagIds: Guid[]) => {
            const foundUgu = value.find((ugu) => ugu.id === userGroupUserId);
            if (foundUgu !== undefined) {
                foundUgu.userTagIds = [...tagIds];
            }

            form.setFieldsValue(
                casterWithIdWrapper<UserGroupsUserView[]>()(id, form, value)
            );
        };
    }

    getUserDataSource = memoizeOne(
        (orgUsersAndUserGroupUsers: UserOrUserGroupUser[]) => {
            return orgUsersAndUserGroupUsers.map((orgUsersOrUserGroupUser) => ({
                key: orgUsersOrUserGroupUser.value.key,
                value: orgUsersOrUserGroupUser.value,
                title: "",
            }));
        }
    );

    render = () => {
        const { existingUserGroupUserIds } = this.state;
        const { form, id, allUsers } = this.props;

        let defaultMembers: UserGroupsUserView[] | undefined;
        switch (this.props.type) {
            case "CREATE":
                break;
            case "EDIT":
                defaultMembers = this.props.defaultMembers;
                break;
            default:
                assertNever(this.props);
        }

        const allTags = this.props.allTags.content.map((tag) => tag.resource);
        const allTagCategories = this.props.allTags.linkedResources;

        // Apart from Transfer and Table props, the rest is boilerplate code copied from https://ant.design/components/transfer/#components-transfer-demo-table-transfer
        return (
            <div className={theme.members}>
                {form.getFieldDecorator(id, {
                    rules: [],
                    preserve: true,
                    trigger: "", // All triggers are handled by direct calls to setFieldsValue.
                    initialValue: defaultMembers
                        ? castValue(id, form, defaultMembers)
                        : undefined,
                    getValueProps: (userGroupUsers) => {
                        const userGroupUsersForTransferComp: UserGroupUserForTransferComp[] =
                            this.mapToUserGroupUsersForTransferComp(
                                userGroupUsers || [],
                                allUsers
                            );
                        const usersForTransferComp: UserForTransferComp[] =
                            allUsers.map((userOverview) => {
                                return {
                                    type: "User",
                                    value: {
                                        key: userOverview.id.toString(),
                                        userOverview,
                                    },
                                };
                            });
                        const usersOrGroups: UserOrUserGroupUser[] =
                            userGroupUsersForTransferComp
                                .map((ugu) => ugu as UserOrUserGroupUser)
                                .concat(usersForTransferComp);
                        const usersDataSource: TransferItem[] =
                            this.getUserDataSource(usersOrGroups);
                        return { dataSource: usersDataSource };
                    },
                })(
                    <Transfer
                        targetKeys={existingUserGroupUserIds}
                        titles={[
                            <>
                                {t(
                                    "UserManagement_Groups_Transfer_Header_Userlist"
                                )}
                            </>,
                            <>
                                {t(
                                    "UserManagement_Groups_Transfer_Header_Users_Added"
                                )}
                            </>,
                        ]}
                        showSelectAll={false}
                        listStyle={{
                            width: 260,
                            height: 450,
                        }}
                        showSearch
                        operations={[
                            t("UserManagement_Groups_Add"),
                            t("UserManagement_Groups_Remove"),
                        ]}
                        filterOption={(inputValue, item) =>
                            this.searchForUser(item, inputValue)
                        }
                        onChange={this.handleChange}
                        className={theme.overriddenTransferWrapper}
                        locale={{
                            itemUnit: t(
                                "UserManagement_Groups_Transfer_Table_Unit"
                            ),
                            itemsUnit: t(
                                "UserManagement_Groups_Transfer_Table_Unit_Plural"
                            ),
                            notFoundContent: (
                                <>
                                    {t(
                                        "UserManagement_Groups_Transfer_Table_Empty"
                                    )}
                                </>
                            ),
                            searchPlaceholder: t(
                                "UserManagement_Groups_Transfer_Table_Search"
                            ),
                        }}
                    >
                        {({
                            direction,
                            filteredItems,
                            onItemSelectAll,
                            onItemSelect,
                            selectedKeys: listSelectedKeys,
                        }) => {
                            const onCell = (item: TransferItem) => {
                                const key = item?.key;
                                if (!key) {
                                    throw Error(
                                        "Expected key to be of type string, got undefined"
                                    );
                                }
                                return {
                                    onClick: () => {
                                        onItemSelect(
                                            key,
                                            !listSelectedKeys.includes(key)
                                        );
                                    },
                                };
                            };
                            const leftTableColumns: ColumnType<TransferItem>[] =
                                [
                                    {
                                        dataIndex: "value.userOverview",
                                        title: (
                                            <>
                                                {t(
                                                    "UserManagement_Groups_Transfer_Table_User_Column"
                                                )}
                                            </>
                                        ),
                                        render: this.renderUserInfo(),
                                        className: theme.header,
                                        onCell,
                                    },
                                ];
                            const rightTableColumns: ColumnType<TransferItem>[] =
                                [
                                    {
                                        dataIndex: "value.userOverview",
                                        title: (
                                            <>
                                                {t(
                                                    "UserManagement_Groups_Transfer_Table_User_Column"
                                                )}
                                            </>
                                        ),
                                        render: this.renderUserInfo(),
                                        className: theme.header,
                                        onCell,
                                    },
                                    {
                                        title: (
                                            <>
                                                {t(
                                                    "UserManagement_Groups_Transfer_Table_Tags_Column"
                                                )}
                                            </>
                                        ),
                                        render: this.renderUserTags(
                                            allTags,
                                            allTagCategories
                                        ),
                                        className: theme.header,
                                    },
                                ];
                            const columns =
                                direction === "left"
                                    ? leftTableColumns
                                    : rightTableColumns;
                            const rowSelection = {
                                onSelectAll(
                                    selected: boolean,
                                    selectedRows: TransferItem[]
                                ) {
                                    const treeSelectedKeys = selectedRows.map(
                                        ({ key }: { key?: string }) => {
                                            if (key === undefined) {
                                                throw Error(
                                                    "selected row key should not be undefined"
                                                );
                                            } // Should never occur
                                            return key;
                                        }
                                    );

                                    const diffKeys = selected
                                        ? difference(
                                              treeSelectedKeys,
                                              listSelectedKeys
                                          )
                                        : difference(
                                              listSelectedKeys,
                                              treeSelectedKeys
                                          );
                                    onItemSelectAll(diffKeys, selected);
                                },
                                onSelect(
                                    { key }: { key?: string },
                                    selected: boolean
                                ) {
                                    if (key === undefined) {
                                        throw Error(
                                            "onSelect `key` should not be undefined"
                                        );
                                    } // Should never occur
                                    onItemSelect(key, selected);
                                },
                                selectedRowKeys: listSelectedKeys,
                            };

                            return (
                                <Table
                                    rowSelection={rowSelection}
                                    columns={columns}
                                    dataSource={filteredItems}
                                    size="small"
                                    pagination={false}
                                    scroll={{ y: 300 }}
                                    className={theme.fixedHeight}
                                />
                            );
                        }}
                    </Transfer>
                )}
            </div>
        );
    };

    // Basic search provided by antd example. We just improved a bit to search with no case sensitive and also search by email
    private searchForUser(item: TransferItem, inputValue: string): boolean {
        const user: UserOverview = item.value.userOverview;
        return (
            formatUserName(user)
                .toLowerCase()
                .indexOf(inputValue.toLowerCase()) !== -1 ||
            item.value.userOverview.email
                .toLowerCase()
                .indexOf(inputValue.toLowerCase()) !== -1
        );
    }

    private mapToUserGroupUsersForTransferComp(
        userGroupUsers: UserGroupsUserView[],
        users: UserOverview[]
    ): Array<UserGroupUserForTransferComp> {
        return userGroupUsers.map((userGroupUser) => {
            const user = users.find((u) => u.id === userGroupUser.userId);
            if (user === undefined) {
                throw logAndThrow(
                    "It couldn't find the equivalent user with id " +
                        userGroupUser.userId +
                        "for user group user: " +
                        userGroupUser.id
                );
            } else {
                return {
                    type: "UserGroupUser",
                    value: {
                        key: userGroupUser.id,
                        userGroupsUserView: userGroupUser,
                        userOverview: user,
                        userTagIds: userGroupUser.userTagIds,
                    },
                };
            }
        });
    }
}

class UserGroupMembers extends React.PureComponent<
    UserGroupDetailsProps,
    UserGroupDetailsState
> {
    state: UserGroupDetailsState = { status: { type: "DEFAULT" } };
    asyncComponent =
        React.createRef<
            AsyncComponent3<
                UserGroupWithDetails,
                UserListResponseOverview,
                Tags
            >
        >();
    membersForm: UserGroupMembersFormComp | null = null;

    render = () => {
        const { ajaxManager, navigate, id: userGroupId } = this.props;

        return (
            <AsyncComponent3<
                UserGroupWithDetails,
                UserListResponseOverview,
                Tags
            >
                ajaxManager={ajaxManager}
                promises={[
                    () => getUserGroupsGroupId(Number(userGroupId)),
                    () =>
                        getUsers({ format: "Overview" }).then((response) => {
                            if (response.data.type !== "Overview") {
                                throw new Error(
                                    "Expected an overview user list"
                                );
                            }
                            return { ...response, data: response.data };
                        }),
                    getTags,
                ]}
                ref={this.asyncComponent}
                renderLoading={() => <PageSpinner />}
                renderError={(code) => {
                    return (
                        <UserGroupsApplicationBar
                            currentTabId={USER_GROUP_DETAILS_TAB}
                        >
                            <LoggedInHTTPError code={code} />
                        </UserGroupsApplicationBar>
                    );
                }}
                key={userGroupId}
                renderContent={(
                    userGroupWithDetails: UserGroupWithDetails,
                    userListResponseOverview: UserListResponseOverview,
                    allTags: Tags
                ) => {
                    const {
                        content: { resource: userGroup, actions },
                    } = userGroupWithDetails;
                    const orgUsers = userListResponseOverview.data.content.map(
                        (dto) => dto.resource
                    );
                    const editable = actions.admin || actions.edit;

                    let userGroupMembersForm = <></>;
                    if (!editable) {
                        userGroupMembersForm = (
                            <FormFrame>
                                <NoAccessCard />
                            </FormFrame>
                        );
                    } else {
                        userGroupMembersForm = (
                            <FormFrame>
                                <UserGroupMembersForm
                                    // Need to use this because FormCreate gives a reference to the wrapper
                                    // (as opposed to the UserGroupMembersFormComp)
                                    wrappedComponentRef={(
                                        form: UserGroupMembersFormComp
                                    ) => {
                                        this.membersForm = form;
                                    }}
                                    onSave={(
                                        userGroupsUserView: UserGroupsUserView[]
                                    ) => {
                                        const data: UpdateUserGroupBody = {
                                            users: userGroupsUserView,
                                        };
                                        ajaxManager
                                            .ajax<
                                                UserGroupWithDetails,
                                                UserGroupEditorStatus
                                            >(
                                                () =>
                                                    patchUserGroupsGroupId(
                                                        userGroup.id,
                                                        data
                                                    ),
                                                {
                                                    component: this,
                                                    initialStatusValue:
                                                        "DEFAULT",
                                                    inFlightStatus: {
                                                        type: "SUBMITTING",
                                                    },
                                                    getSuccessStatus: (
                                                        _resp
                                                    ) => ({ type: "DEFAULT" }),
                                                    getErrorStatus: (
                                                        _code
                                                    ) => ({ type: "DEFAULT" }),
                                                }
                                            )
                                            .then((_userGroupWithDetails) => {
                                                emitNotification(
                                                    "success",
                                                    t(
                                                        "UserManagement_Groups_Transfer_Successful_Update"
                                                    )
                                                );
                                                navigate(WebRoute.USER_GROUPS);
                                            });
                                    }}
                                    defaultMembers={
                                        userGroupWithDetails.content.resource
                                            .userGroupUsers
                                    }
                                    availableUserTagsAndCategories={allTags}
                                    allUsers={orgUsers}
                                    ajaxManager={ajaxManager}
                                />
                            </FormFrame>
                        );
                    }

                    return (
                        <UserGroupsApplicationBar
                            userGroup={userGroup}
                            currentTabId={USER_GROUP_MEMBERS_TAB}
                        >
                            <FormFrame>
                                <Spin spinning={this.showSpinner()}>
                                    {userGroupMembersForm}
                                </Spin>
                                <Button
                                    id="user-group-manage-members-update"
                                    className={theme.stepButton}
                                    variant="primary"
                                    onClick={() => {
                                        if (this.membersForm !== null) {
                                            this.membersForm.save();
                                        }
                                    }}
                                >
                                    {t("UserManagement_Groups_Save_And_Close")}
                                </Button>
                                <Button
                                    id="user-group-manage-members-cancel"
                                    className={theme.stepButton}
                                    variant="default"
                                    onClick={() =>
                                        navigate(WebRoute.USER_GROUPS)
                                    }
                                >
                                    {t("Cancel")}
                                </Button>
                            </FormFrame>
                        </UserGroupsApplicationBar>
                    );
                }}
            />
        );
    };

    showSpinner(): boolean {
        const { status } = this.state;
        switch (status.type) {
            case "SUBMITTING":
                return true;
            case "DEFAULT":
                return false;
            default:
                assertNever(status);
        }
    }
}

interface UserGroupMembersFormProps extends FormRootComponentProps<UserGroupCreateMembersValues> {
    availableUserTagsAndCategories: Tags;
    ajaxManager: AjaxManager;
    allUsers: UserOverview[];
    defaultMembers: UserGroupsUserView[];
    onSave: (users: UserGroupsUserView[]) => void;
}

class UserGroupMembersFormComp extends React.PureComponent<UserGroupMembersFormProps> {
    render = () => {
        const {
            form,
            ajaxManager,
            defaultMembers,
            availableUserTagsAndCategories,
            allUsers,
        } = this.props;
        return (
            <UserTableTransfer
                id="members"
                type="EDIT"
                allTags={availableUserTagsAndCategories}
                form={form}
                defaultMembers={defaultMembers}
                ajaxManager={ajaxManager}
                allUsers={allUsers}
            />
        );
    };

    save = () => {
        const { form, onSave } = this.props;
        const userGroupUsers = form.getFieldValue("members") || [];
        onSave(userGroupUsers);
    };
}

const UserGroupDetailsMembers = withRouter(
    withIdParams(withI18n<UserGroupDetailsProps>(UserGroupMembers))
);
const UserGroupMembersForm = FormCreate<
    UserGroupCreateMembersValues,
    UserGroupMembersFormProps
>()(UserGroupMembersFormComp);

export {
    UserGroupDetailsMembers,
    UserTableTransfer,
    UserListResponseOverview,
    type UserForTransferComp,
    type UserGroupTransferValues,
};
